import { z } from 'zod';
import { ATTACHMENT_CONTENT_TYPES } from '../constants/attachment.constant';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { calendarDateSchema } from './arrangement.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §2] AN UPLOADED PA — AN ORDER THAT EXISTS ONLY AS PAPER.
 *
 *  ⚠️ IT IS NOT AN `Attachment`, AND `attachment.model.ts`'s OWN HEADER IS THE REFUSAL.
 *  That column IS the join and the scope IS the parent's: every attachment read goes through
 *  `requireArrangement` first, so FR-3.6 is impossible to forget there. Making `arrangement_id`
 *  nullable would turn a scope decision into an `if` at three call sites — a scope expressed as
 *  a condition, which §10 says must be impossible to forget rather than merely remembered.
 *
 *  ⚠️ AND IT IS NOT A THIRD `ARRANGEMENT_KINDS` MEMBER. That enum is consumed by `z.enum` at
 *  four sites here and one in `fill.schema.ts`; widening it puts a row with no duty points, no
 *  policy and no allocations into every query that already reads `arrangements` — the dashboard
 *  spine, `previewPublish`, `restampArrangement`, `masterArchive`, `outstandingByArrangement` and
 *  the standing-order generator. It would also burn a `Counter` per scanned PDF for a `reference`
 *  nothing needs.
 *
 *  ⚠️ THE TITLE AND THE ORDER DATE ARE WHY IT IS ITS OWN THING. An attachment has a FILENAME and
 *  nothing else — the operator's own name for the bytes. §2 asks for a title somebody types and a
 *  date the order was issued on, which are facts about the ORDER rather than about the file.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const uploadedOrderSchema = z.object({
  id: objectIdSchema,
  /** What an operator will look for it by. Typed, never derived from the filename. */
  title: z.string(),
  /**
   * ⚠️ A BUSINESS DATE, SO A STRING — §7's rule, and the same shape as `arrangements.pa_date`.
   * Lexicographic order is chronological order and a range filter is a plain string range.
   */
  orderDate: calendarDateSchema,
  filename: z.string(),
  contentType: z.enum(ATTACHMENT_CONTENT_TYPES),
  sizeBytes: z.number().int().min(0),
  /**
   * ⚠️ A REQUIRED KEY WITH A NULLABLE VALUE — §6's usual rule. The list renders an "Added by" row
   * either way, and an officer deactivated since the upload still resolves.
   */
  uploadedBy: z.object({ id: objectIdSchema, name: z.string() }).nullable(),
  uploadedAt: z.string(),
});
export type UploadedOrderDto = z.infer<typeof uploadedOrderSchema>;

/**
 * ⚠️ PAGINATED, UNLIKE AN ARRANGEMENT'S ATTACHMENTS — AND THE DIFFERENCE IS THE BOUND.
 *
 * `attachmentListSchema` is bounded and unpaginated because an order carries at most ten files
 * and a page of them is not a coherent answer. An establishment-wide library of scanned orders
 * has no such ceiling: it grows for as long as the deployment runs, so §7's "every list query is
 * paginated and bounded" applies with no exception.
 */
export const UPLOADED_ORDER_SORT_FIELDS = ['orderDate', 'title', 'createdAt'] as const;
export type UploadedOrderSortField = (typeof UPLOADED_ORDER_SORT_FIELDS)[number];

export const listUploadedOrdersQuerySchema = createPaginationSchema(UPLOADED_ORDER_SORT_FIELDS, 'orderDate').extend({
  search: z.string().trim().min(1).max(120).optional(),
  dateFrom: calendarDateSchema.optional(),
  dateTo: calendarDateSchema.optional(),
});
export type ListUploadedOrdersQuery = z.infer<typeof listUploadedOrdersQuerySchema>;

/**
 * ⚠️ THE MULTIPART FIELDS, PARSED BY `validateUploadFields` RATHER THAN BY `validate`.
 *
 * A multipart body is a STREAM that must be bounded while it arrives, so it never reaches
 * `res.locals.validated` — `attachment.schema.ts` records that at length. What §2 adds is that
 * the stream now carries TEXT fields beside the file, and those still have to be parsed by zod
 * somewhere: `upload.middleware.ts` collects them and this is what it parses them with. One
 * parse, in one place, and never `req.body`.
 */
export const createUploadedOrderSchema = z.object({
  title: z.string().trim().min(1).max(200),
  orderDate: calendarDateSchema,
});
export type CreateUploadedOrderInput = z.infer<typeof createUploadedOrderSchema>;

/**
 * ⚠️ NO `.default()` ON THE BASE — §6's `.partial()` rule, which `updateSchema.test.ts` checks
 * over the whole barrel. In zod 4 a `.partial()`-ed default still fires on a missing key, which
 * silently defeats the non-empty refine every update schema relies on.
 */
export const updateUploadedOrderSchema = createUploadedOrderSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'Provide at least one field to update' });
export type UpdateUploadedOrderInput = z.infer<typeof updateUploadedOrderSchema>;
